/**
 * Where somebody who is not a user of the agency reads their agreement and signs it.
 *
 * The person on this screen is a tenant, a landlord or a guarantor. They do not work for the
 * agency, they have no agency session, and this screen must never become a way into anything
 * except the agreements they are themselves named on. Everything here goes through
 * `usePortal()` — the portal's own client, its own bearer token, its own router — and nothing in
 * this file imports `useSession`.
 *
 * **The signing box is deliberately unglamorous, and that is the design.** There is no canvas to
 * draw on and no cursive typeface. What is being recorded is a name the person typed, the moment
 * they typed it, the account they were signed in as, and the fingerprint of the exact document in
 * front of them — and the screen says so in those words, above the box, before anybody types
 * anything. A drawn squiggle would look more like a signature and be worth strictly less; see the
 * long note in `packages/core/src/agreements.ts`.
 *
 * **The digest the screen rendered goes back with the signature.** If the document has moved
 * between loading it and signing it, the server refuses with a 409 and the reader is asked to read
 * it again. Nobody signs wording they were not shown.
 *
 * **The whole document is on the page before the box.** Not a summary, not a link to a PDF, not an
 * "I have read and agree" over a scroll box of three lines. Somebody being asked to agree to
 * something is entitled to have it in front of them, and the button is at the bottom of it.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { PAPER_SIGNATURE_STATEMENT, formatDate } from "@rentorng/core";
import { Alert, Badge, Button, Card, CardTitle, Checkbox, Detail, EmptyState, Field, Input, Spinner, Table } from "@/components/ui";
import { IconSignature } from "@/components/icons";
import { usePageTitle } from "@/lib/title";
import { PortalProvider, usePortal } from "@/lib/portal";
import { PortalShell } from "./PortalShell";
import { PortalSignIn } from "./PortalAuth";

/* ------------------------------------------------------------------- shapes */

export interface PortalAgreementRow {
  id: string;
  reference: string;
  status: string;
  issuedOn: string;
  propertyName: string | null;
  unitLabel: string | null;
  role: string;
  roleLabel: string;
  signedAt: string | null;
  /* How their own block was signed. `signedAt` says only that it is no longer outstanding, and a
     badge drawn from that alone reads a name somebody typed and a photograph of a page the same
     way. ADR-0149 §4. */
  signatureKind: string | null;
  witnessed: boolean | null;
  /** The one thing this list exists to say. */
  awaitingMe: boolean;
  signing: {
    total: number;
    signed: number;
    outstanding: number;
    complete: boolean;
    summary: string;
    onPaper: number;
    anyOnPaper: boolean;
    paperSummary: string | null;
  };
}

interface PortalAgreementDetail {
  id: string;
  reference: string;
  status: string;
  issuedOn: string;
  sentAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  currency: string;
  digest: string;
  fingerprint: string;
  altered: boolean;
  signing: {
    total: number;
    signed: number;
    outstanding: number;
    complete: boolean;
    summary: string;
    waitingOn: string[];
    /** What the count is made of. "3 of 3 signed" says nothing about how any of them was given. */
    witnessed: number;
    onPaper: number;
    anyOnPaper: boolean;
    paperSummary: string | null;
  };
  me: {
    role: string;
    roleLabel: string;
    name: string;
    signedAt: string | null;
    signedName: string | null;
    canSign: boolean;
    /** Whether they may send back a copy they signed by hand. A second decision, not the same one. */
    canUploadPaper: boolean;
    signatureKind: string | null;
    signatureKindLabel: string | null;
    witnessed: boolean | null;
    paper: {
      documentId: string | null;
      signedOn: string | null;
      source: string | null;
      sourceLabel: string;
      recordedAt: string | null;
      recordedBy: string | null;
      held: boolean;
    } | null;
  };
  parties: Array<{
    role: string;
    roleLabel: string;
    name: string;
    signedAt: string | null;
    signedName: string | null;
    /* Every party is told how every other party signed — an agreement where two of the three
       signatures are scans is a different thing to put your name to. The files themselves are not
       shared: somebody downloads their own copy and nobody else's. */
    signatureKind: string | null;
    signatureKindLabel: string | null;
    witnessed: boolean | null;
    paperSignedOn: string | null;
  }>;
  document: {
    title: string;
    subject: string;
    issuedOnLabel: string;
    recitals: string[];
    particulars: Array<[string, string]>;
    schedule: Array<{ label: string; when: string; amountLabel: string }>;
    totals: { commencementLabel: string; perPeriodLabel: string; periodSuffix: string };
    conditions: string[];
    notes: string | null;
    signatureStatement: string;
    supersedes: string | null;
  };
}

/* ---------------------------------------------------- on the account screen */

/**
 * The agreements block on the portal's account screen.
 *
 * Drawn only when there is something in it — a "Your agreements" heading over an empty panel tells
 * somebody they are missing something they never had, which is the rule every half of that screen
 * follows.
 */
export function AgreementSummaries({ agreements }: { agreements: PortalAgreementRow[] }) {
  if (!agreements.length) return null;
  const waiting = agreements.filter((row) => row.awaitingMe);

  return (
    <Card className="mb-5">
      <CardTitle>Your agreements</CardTitle>
      {waiting.length ? (
        <Alert tone="warn">
          {waiting.length === 1
            ? "One agreement is waiting for your signature."
            : `${waiting.length} agreements are waiting for your signature.`}
        </Alert>
      ) : null}

      <div className="space-y-3">
        {agreements.map((row) => (
          <div
            key={row.id}
            data-testid="portal-agreement-row"
            className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-control)] border border-ink-200 p-3"
          >
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-medium">
                <IconSignature className="size-4 text-ink-400" />
                {[row.propertyName, row.unitLabel].filter(Boolean).join(" · ") || row.reference}
              </p>
              <p className="mt-0.5 text-sm text-ink-500">
                {row.reference} · you are the {row.roleLabel.toLowerCase()} · {row.signing.summary}
                {row.signing.anyOnPaper ? ` · ${row.signing.paperSummary}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {row.awaitingMe ? (
                <Badge tone="warn" dot>
                  Needs you
                </Badge>
              ) : row.signedAt && row.witnessed === false ? (
                /*
                  **Not "You signed".**

                  A green tick over a block nobody typed is "signed by everybody" over an
                  unwitnessed party again, in less room — and this is the screen a party actually
                  lands on, so it is the one place the claim is most likely to be read on its own.
                  What they did was send a copy, and that is what the badge says.
                */
                <Badge tone="info">Copy uploaded</Badge>
              ) : row.signedAt ? (
                <Badge tone="success">You signed</Badge>
              ) : null}
              <Link to={`/portal/agreements/${row.id}`} className="btn btn-secondary btn-sm">
                {row.awaitingMe ? "Read and sign" : "Open"}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------- one agreement */

function PortalAgreement() {
  const { id = "" } = useParams();
  const { get, post, loading, me, token } = usePortal();
  const [data, setData] = useState<PortalAgreementDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signError, setSignError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  /* The paper half: a second way to answer the same question, kept in its own state so that an
     error on one does not clear the other's form under somebody who was mid-way through it. */
  const [showPaper, setShowPaper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [paperError, setPaperError] = useState<string | null>(null);
  const paperFile = useRef<HTMLInputElement>(null);

  usePageTitle(data ? `Agreement ${data.reference}` : "Your agreement");

  const load = useCallback(() => {
    setError(null);
    get<PortalAgreementDetail>(`/agreements/${id}`)
      .then(setData)
      .catch((problem: unknown) =>
        setError(problem instanceof Error ? problem.message : "That agreement could not be opened."),
      );
  }, [get, id]);

  useEffect(() => {
    if (loading || !me) return;
    load();
  }, [load, loading, me]);

  if (loading) return <PortalShell><Spinner /></PortalShell>;
  if (!me) return <PortalSignIn />;

  if (error) {
    return (
      <PortalShell>
        <EmptyState title="That agreement could not be opened" hint={error} action={<Link className="btn btn-secondary" to="/portal">Back to your account</Link>} />
      </PortalShell>
    );
  }
  if (!data) return <PortalShell><Spinner /></PortalShell>;

  const doc = data.document;

  /*
    The file is fetched, not linked to.

    A portal session is a bearer token in `localStorage`, deliberately — a tenant may well be signed
    into their own agency's app in the same browser, and one cookie name cannot mean two things. The
    consequence is that an `<a href>` carries no credential at all: a plain download link answers
    401 for the very person it belongs to. So the bytes are fetched with the token, handed to the
    browser as a blob, and the object URL is revoked immediately afterwards.
  */
  async function download() {
    if (!data) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch(`/api/portal/agreements/${data.id}.pdf`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("That copy could not be prepared. Try again.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Agreement ${data.reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (problem) {
      setDownloadError(problem instanceof Error ? problem.message : "That copy could not be prepared.");
    } finally {
      setDownloading(false);
    }
  }

  /**
   * Send back the copy they printed and signed by hand.
   *
   * **Deliberately not styled as a second signing box.** It sends a file and a day, its button says
   * "Send the signed copy", and the paragraph above it says in plain words that what will be
   * recorded is an uploaded copy rather than a signature given here. Somebody choosing between the
   * two controls must be able to tell that they are choosing between two different things.
   *
   * Sent by hand rather than through the portal client, for the reason every upload in this product
   * is: `post()` sends JSON, and base64 would make a phone photograph a third bigger on the
   * connection that is already the slow part. The bearer token goes on the request explicitly,
   * because a portal session is a token in `localStorage` and not a cookie — see `download` above.
   */
  async function sendPaper(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data) return;
    const chosen = paperFile.current?.files?.[0];
    if (!chosen) {
      setPaperError("Choose the signed copy to upload.");
      return;
    }

    const fields = new FormData(event.currentTarget);
    const form = new FormData();
    form.append("file", chosen);
    form.append("signedOn", String(fields.get("signedOn") ?? ""));
    /* The fingerprint of what is on this screen, sent with the file exactly as it is sent with a
       typed name. A page printed before the document moved is not a copy of the document as it
       now stands, and the server refuses rather than recording it as though it were. */
    form.append("digest", data.digest);

    setUploading(true);
    setPaperError(null);
    try {
      const response = await fetch(`/api/portal/agreements/${data.id}/paper`, {
        method: "POST",
        body: form,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "That copy could not be recorded.");
      }
      setData((await response.json()) as PortalAgreementDetail);
      setShowPaper(false);
    } catch (problem) {
      setPaperError(problem instanceof Error ? problem.message : "That copy could not be recorded.");
    } finally {
      setUploading(false);
    }
  }

  /**
   * Their own uploaded copy, fetched back.
   *
   * Fetched rather than linked to, for the same reason the PDF is: a portal session is a bearer
   * token in `localStorage` and a plain `<a href>` carries no credential, so a download link would
   * answer 401 for the very person it belongs to.
   */
  async function openCounterpart() {
    if (!data) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch(`/api/portal/agreements/${data.id}/counterpart`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("That copy could not be opened. Contact the agency.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noreferrer");
      /* Left for the new tab to read before it is revoked. Revoking immediately, as the PDF
         download does, works only because that one is consumed by a click on the same page. */
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (problem) {
      setDownloadError(problem instanceof Error ? problem.message : "That copy could not be opened.");
    } finally {
      setDownloading(false);
    }
  }

  async function sign(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setSignError(null);
    try {
      const next = await post<PortalAgreementDetail>(`/agreements/${data.id}/sign`, {
        signedName: String(form.get("signedName") ?? ""),
        /* The fingerprint of what is on this screen, sent back with the signature. The server
           refuses it if the document has since moved. */
        digest: data.digest,
        agreed: true,
      });
      setData(next);
      setAgreed(false);
    } catch (problem) {
      setSignError(problem instanceof Error ? problem.message : "That could not be signed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PortalShell agency={me.agency}>
      <Link to="/portal" className="mb-4 inline-block text-sm text-ink-500 hover:text-ink-800">
        ← Your account
      </Link>

      <h1 className="text-2xl font-semibold">{doc.title}</h1>
      <p className="mt-1 text-ink-600">{doc.subject}</p>
      <p className="mt-1 text-sm text-ink-500">
        {data.reference} · dated {doc.issuedOnLabel}
      </p>

      {data.altered ? (
        <Alert tone="danger" title="This does not match the document that was sent to you">
          Do not sign this. The terms held for this agreement no longer produce the fingerprint recorded when it was
          issued. Contact the agency.
        </Alert>
      ) : null}

      {data.status === "cancelled" ? (
        <Alert tone="warn" title="Withdrawn">This agreement was withdrawn and no longer stands.</Alert>
      ) : null}
      {data.status === "superseded" ? (
        <Alert tone="muted" title="Replaced">A later agreement replaced this one. Your signature on it is kept.</Alert>
      ) : null}
      {data.status === "signed" ? (
        <Alert tone="success" title="Signed by everybody">
          Every party has signed. You can download a copy at any time.
        </Alert>
      ) : null}

      {/*
        What the signatures on this document are made of.

        Shown to every party, signed or not, because it is part of what they are being asked to put
        their name to. "Signed by everybody" over a party this product never watched sign is exactly
        the sentence ADR-0126 is careful about, and leaving the qualification to a column further
        down would let the good news be read on its own.
      */}
      {data.signing.anyOnPaper ? (
        <Alert tone="info" title="Not every signature on this was given here">
          <span data-testid="portal-paper-notice">
            {data.signing.paperSummary} — a file uploaded and asserted to be this agreement signed by hand. Nothing in
            it has been compared with this document's fingerprint, and it was not recorded here as it happened.
          </span>
        </Alert>
      ) : null}

      <Card className="mt-5">
        <CardTitle>Who has signed</CardTitle>
        <p className="mb-3 text-sm text-ink-600">{data.signing.summary}</p>
        <Table
          head={
            <tr>
              <th>Party</th>
              <th>Name</th>
              <th>How</th>
              <th>Signed</th>
            </tr>
          }
        >
          {data.parties.map((party, index) => (
            <tr key={`${party.role}-${index}`}>
              <td>{party.roleLabel}</td>
              <td>{party.name}</td>
              <td className="whitespace-nowrap">
                {!party.signedAt ? (
                  <span className="text-ink-500">—</span>
                ) : party.witnessed ? (
                  <Badge tone="success">Typed here</Badge>
                ) : (
                  <Badge tone="info">
                    <span data-testid="portal-kind-on-paper">On paper</span>
                  </Badge>
                )}
              </td>
              <td>
                {party.signedAt ? (
                  <Badge tone="success">
                    {formatDate((party.witnessed === false && party.paperSignedOn ? party.paperSignedOn : party.signedAt.slice(0, 10)))}
                  </Badge>
                ) : (
                  <span className="text-ink-500">Not yet</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </Card>

      <Card className="mt-5">
        <CardTitle>The agreement</CardTitle>
        {doc.recitals.map((line, index) => (
          <p key={index} className="mb-3 text-sm leading-relaxed text-ink-800">
            {line}
          </p>
        ))}

        <h2 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500">The particulars</h2>
        <dl className="grid gap-2 sm:grid-cols-2">
          {doc.particulars.map(([label, value]) => (
            <Detail key={label} label={label}>
              {value}
            </Detail>
          ))}
        </dl>

        <h2 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500">What is payable</h2>
        <Table
          head={
            <tr>
              <th>Charge</th>
              <th>When</th>
              <th className="num">Amount</th>
            </tr>
          }
        >
          {doc.schedule.map((line, index) => (
            <tr key={index}>
              <td className="font-medium">{line.label}</td>
              <td>{line.when}</td>
              <td className="num tabular">{line.amountLabel}</td>
            </tr>
          ))}
        </Table>
        <p className="mt-3 text-right text-sm">
          <span className="text-ink-500">Payable before you take occupation: </span>
          <span className="text-base font-semibold">{doc.totals.commencementLabel}</span>
        </p>

        <h2 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500">Conditions</h2>
        <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink-800">
          {doc.conditions.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ol>

        {doc.notes ? (
          <>
            <h2 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500">Further terms</h2>
            <p className="text-sm leading-relaxed text-ink-800">{doc.notes}</p>
          </>
        ) : null}
      </Card>

      {/* ------------------------------------------------------------ signing */}

      <Card className="mt-5">
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            <IconSignature size={18} /> Your signature
          </span>
        </CardTitle>

        {data.me.signedAt && data.me.witnessed ? (
          <div>
            <p className="text-sm text-ink-600">You signed this as</p>
            <p className="mt-1 text-xl font-semibold" data-testid="portal-signed-name">
              {data.me.signedName}
            </p>
            <p className="mt-1 text-sm text-ink-500">
              on {formatDate(data.me.signedAt.slice(0, 10), { long: true })}
            </p>
          </div>
        ) : data.me.signedAt ? (
          /*
            Their own block, signed on paper.

            No name set large where a typed signature would sit, on this screen for the same reason
            as on the printed page: nobody typed one, and drawing their own name there would
            manufacture the look of a signature out of a record. What is shown is what is held, and
            the file they sent is offered back to them.
          */
          <div>
            <p className="text-sm text-ink-600" data-testid="portal-paper-mine">
              A copy you signed by hand was uploaded for this agreement. It is recorded as an uploaded copy, asserted
              to be signed — not as a signature given here.
            </p>
            <p className="mt-1 text-sm text-ink-500">
              {data.me.paper?.signedOn
                ? `Dated ${formatDate(data.me.paper.signedOn, { long: true })} on the paper`
                : "No day was recorded"}
              {data.me.paper?.sourceLabel ? ` · ${data.me.paper.sourceLabel.toLowerCase()}` : ""}
            </p>
            {data.me.paper?.held ? (
              <Button
                variant="secondary"
                className="mt-3"
                onClick={openCounterpart}
                disabled={downloading}
                data-testid="portal-paper-open"
              >
                Open the copy that was uploaded
              </Button>
            ) : null}
          </div>
        ) : data.me.canSign || data.me.canUploadPaper ? (
          <>
            {data.me.canSign ? (
              <form onSubmit={sign}>
                {signError ? <Alert tone="danger">{signError}</Alert> : null}
                <p className="mb-4 text-sm leading-relaxed text-ink-600">{doc.signatureStatement}</p>
                <Checkbox
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                  label="I have read this agreement and I agree to it."
                  hint="Nobody can sign on your behalf. If anything above is wrong, do not sign — tell the agency instead."
                />
                <div className="mt-4">
                  <Field
                    label="Type your name to sign"
                    htmlFor="signedName"
                    hint="Your own name, as you would write it."
                  >
                    <Input id="signedName" name="signedName" placeholder={data.me.name} required autoComplete="off" />
                  </Field>
                </div>
                <Button type="submit" className="mt-4" disabled={!agreed || busy} data-testid="portal-sign">
                  {busy ? "Signing…" : "Sign this agreement"}
                </Button>
              </form>
            ) : null}

            {/*
              The paper route, below the typed one and visibly a different thing.

              **Not a second signing box.** It is folded away behind a link, its own paragraph says
              what will actually be recorded, and its button says "Send the signed copy" rather than
              "Sign". Somebody choosing between the two has to be able to see that they are choosing
              between a signature this product records as it happens and a file it will hold on
              somebody's word — and the wording here is the only place they will be told before they
              choose.
            */}
            {data.me.canUploadPaper ? (
              <div className="mt-6 border-t border-ink-200 pt-5">
                {showPaper ? (
                  <form onSubmit={sendPaper}>
                    {paperError ? <Alert tone="danger">{paperError}</Alert> : null}
                    <p className="mb-4 text-sm leading-relaxed text-ink-600">
                      Print this agreement, sign it by hand, and send the signed page back here. What is recorded is
                      <strong> an uploaded copy asserted to be signed</strong> — not a signature given in this account.
                      Nothing in your file is compared with this document's fingerprint, and the agreement will say so
                      wherever your signature appears on it. Signing above, by typing your name, records more.
                    </p>
                    <Field label="The signed copy" htmlFor="paperFile" hint="A PDF or a photograph, up to 15MB.">
                      <input
                        id="paperFile"
                        ref={paperFile}
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/webp"
                        className="block w-full text-sm"
                        required
                      />
                    </Field>
                    <div className="mt-3">
                      <Field
                        label="The day you signed it"
                        htmlFor="paperSignedOn"
                        hint="As written on the paper."
                      >
                        <Input
                          id="paperSignedOn"
                          name="signedOn"
                          type="date"
                          /* Nobody signed a document on a day before it existed, and the server
                             says so. The box opens on whichever of today and that day is later,
                             because this browser can be a day behind the agency's own clock. */
                          min={data.issuedOn}
                          defaultValue={
                            new Date().toISOString().slice(0, 10) > data.issuedOn
                              ? new Date().toISOString().slice(0, 10)
                              : data.issuedOn
                          }
                          required
                        />
                      </Field>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button type="submit" disabled={uploading} data-testid="portal-paper-send">
                        {uploading ? "Sending…" : "Send the signed copy"}
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => setShowPaper(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <p className="text-sm text-ink-600">
                    Would you rather sign on paper?{" "}
                    <button
                      type="button"
                      className="link"
                      onClick={() => setShowPaper(true)}
                      data-testid="portal-paper-open-form"
                    >
                      Send back a copy you signed by hand
                    </button>
                    .
                  </p>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-ink-600">
            This agreement is not open for signature. If you were expecting to sign it, contact the agency.
          </p>
        )}

        {/* The same second statement the printed page carries, and only where it is true of this
            document. It is not part of the composed agreement — that text is inside the fingerprint
            every party signed. */}
        {data.signing.anyOnPaper ? (
          <p className="mt-5 border-t border-ink-200 pt-4 text-sm leading-relaxed text-ink-600" data-testid="portal-paper-statement">
            {PAPER_SIGNATURE_STATEMENT}
          </p>
        ) : null}
      </Card>

      <Card className="mt-5">
        <CardTitle>Keep a copy</CardTitle>
        <p className="mb-3 text-sm text-ink-600">
          Your copy carries the same fingerprint as the agency's. Two copies showing different fingerprints are not the
          same document.
        </p>
        <p className="mb-4 font-mono text-sm tracking-wide" data-testid="portal-fingerprint">
          {data.fingerprint}
        </p>
        <Button variant="secondary" onClick={download} disabled={downloading} data-testid="portal-download">
          {downloading ? "Preparing…" : "Download the PDF"}
        </Button>
        {downloadError ? <Alert tone="danger">{downloadError}</Alert> : null}
        <p className="mt-2 text-xs text-ink-500">
          Your copy is fetched with your sign-in, so a copy of this address is no use to anybody else.
        </p>
      </Card>
    </PortalShell>
  );
}

export function PortalAgreementPage() {
  return (
    <PortalProvider>
      <PortalAgreement />
    </PortalProvider>
  );
}
